import React from 'react';

const EMBER_COLORS = ['#fb923c', '#fdba74', '#f97316', '#fbbf24', '#fca5a5'];

// Ambient layered backdrop: grid, animated fire glows, a rising flame base,
// and embers that fly across the entire screen in every direction.
export default function Background() {
  const embers = React.useMemo(
    () =>
      Array.from({ length: 52 }).map((_, i) => ({
        id: i,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        size: 1.5 + Math.random() * 4.5,
        // mostly drift upward (fire rises) with wide horizontal sway
        dx: `${(Math.random() - 0.5) * 280}px`,
        dy: `${-(120 + Math.random() * 520)}px`,
        o: 0.3 + Math.random() * 0.6,
        s: 0.6 + Math.random() * 1.2,
        duration: `${6 + Math.random() * 11}s`,
        delay: `${Math.random() * 12}s`,
        color: EMBER_COLORS[i % EMBER_COLORS.length],
      })),
    []
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Subtle technical grid */}
      <div className="absolute inset-0 bg-grid opacity-70 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />

      {/* Primary fire glow */}
      <div className="absolute left-1/2 top-1/3 h-[42rem] w-[42rem] -translate-x-1/2 -translate-y-1/2 animate-glow-pan rounded-full bg-ember-600/40 blur-[120px]" />
      {/* Pulsing core flame behind the headline */}
      <div className="absolute left-1/2 top-1/3 h-[26rem] w-[26rem] -translate-x-1/2 -translate-y-1/2 animate-flame-flicker rounded-full bg-amber-500/30 blur-[90px]" />
      {/* Secondary warm glow */}
      <div className="absolute bottom-0 left-1/4 h-[28rem] w-[28rem] animate-glow-pan rounded-full bg-amber-500/25 blur-[110px] [animation-delay:-6s]" />
      {/* Deep red counter-light for depth */}
      <div className="absolute -right-20 top-10 h-[26rem] w-[26rem] rounded-full bg-rose-700/20 blur-[120px]" />
      <div className="absolute -left-24 top-1/2 h-[24rem] w-[24rem] rounded-full bg-ember-700/20 blur-[120px]" />

      {/* Rising flame base along the bottom edge */}
      <div className="absolute inset-x-0 bottom-0 h-64 animate-flame-flicker bg-[radial-gradient(120%_100%_at_50%_100%,rgba(249,115,22,0.55),rgba(234,88,12,0.25)_38%,transparent_70%)] blur-2xl" />
      <div className="absolute inset-x-0 bottom-0 h-40 animate-flame-flicker bg-[radial-gradient(90%_100%_at_50%_100%,rgba(253,186,116,0.5),transparent_65%)] blur-xl [animation-delay:-1.5s]" />
      {/* Hot core line at the very bottom */}
      <div className="absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r from-transparent via-ember-400 to-transparent opacity-80 blur-[2px]" />

      {/* Embers flying across the whole screen */}
      {embers.map((e) => (
        <span
          key={e.id}
          className="ember-fly absolute rounded-full"
          style={{
            top: e.top,
            left: e.left,
            width: e.size,
            height: e.size,
            background: e.color,
            boxShadow: `0 0 ${6 + e.size * 2}px ${e.size / 1.5}px ${e.color}`,
            animationDuration: e.duration,
            animationDelay: e.delay,
            '--dx': e.dx,
            '--dy': e.dy,
            '--o': e.o,
            '--s': e.s,
          }}
        />
      ))}

      {/* Vignette (kept light enough that screen-wide embers stay visible) */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_52%,rgba(8,8,10,0.78))]" />
    </div>
  );
}
